import { useState, useEffect, useMemo } from "react"
import { useMap, Source, Layer } from "react-map-gl/mapbox"
import type { Feature, FeatureCollection } from "geojson"
import type { Expression } from "mapbox-gl"
import type { ICommune } from "@/types/explore"

/** Mapping from V2 layer IDs to WFS type codes */
const PRESC_MAP: Record<string, string> = {
  "presc-ebc": "01",
  "presc-er": "05",
  "presc-pdm": "14",
  "presc-ric": "15",
  "presc-pms": "17",
  "presc-oap": "18",
  "presc-emprise": "38",
  "presc-hauteur": "39",
  "presc-volumetrie": "40",
  "presc-stationnement": "44",
}

const INFO_MAP: Record<string, string> = {
  "info-zac": "02",
  "info-zpens": "03",
  "info-dpu": "04",
  "info-div-fonc": "11",
  "info-sursis": "12",
  "info-pae": "13",
  "info-bruit": "14",
  "info-archeo": "16",
  "info-ppr": "21",
  "info-peb": "27",
  "info-taxe": "32",
  "info-sis": "38",
}

const colors: Expression = [
  "match",
  ["get", "common_code"],
  "01",
  "#66C2A4",
  "02",
  "#FCC43E", // warning.main
  "03",
  "#FEB24C",
  "04",
  "#FFE096", // warning.lighter
  "05",
  "#c34c94",
  "11",
  "#7FCDBB",
  "12",
  "#B34262", // magenta.main
  "13",
  "#1D91C0",
  "14",
  "#FFA78F", // error.light
  "15",
  "#253494",
  "16",
  "#081D58",
  "17",
  "#ec823d",
  "18",
  "#33e0ff",
  "19",
  "#CCEBC5",
  "20",
  "#A8DDB5",
  "21",
  "#7BCCC4",
  "27",
  "#788d99",
  "28",
  "#D0D1E6",
  "29",
  "#A6BDDB",
  "30",
  "#74A9CF",
  "31",
  "#3690C0",
  "32",
  "#C95C3F", // error.darker
  "38",
  "#1E40AF", // primary.main
  "39",
  "#A3AED0", // primary.light
  "40",
  "#1B2559", // primary.dark
  "44",
  "#41B6C4",
  "#CCCCCC",
]

interface GeoportailWFSLayerProps {
  activeLayers: string[]
  commune?: ICommune
}

export const GeoportailWFSLayer = ({ activeLayers, commune }: GeoportailWFSLayerProps) => {
  const { current: map } = useMap()

  const [geoJsonSurf, setGeoJsonSurf] = useState<FeatureCollection | undefined>({
    type: "FeatureCollection",
    features: [],
  })
  const [geoJsonPct, setGeoJsonPct] = useState<FeatureCollection | undefined>({
    type: "FeatureCollection",
    features: [],
  })
  const [geoJsonLine, setGeoJsonLine] = useState<FeatureCollection | undefined>({
    type: "FeatureCollection",
    features: [],
  })

  const typepsc = useMemo(() => activeLayers.filter((l) => l in PRESC_MAP).map((l) => PRESC_MAP[l]), [activeLayers])

  const typeinf = useMemo(() => activeLayers.filter((l) => l in INFO_MAP).map((l) => INFO_MAP[l]), [activeLayers])

  useEffect(() => {
    if (!commune || !commune.pluDocument || (!typepsc?.length && !typeinf?.length)) {
      setGeoJsonSurf({ type: "FeatureCollection", features: [] })
      setGeoJsonPct({ type: "FeatureCollection", features: [] })
      setGeoJsonLine({ type: "FeatureCollection", features: [] })
      return
    }

    const emptyCollection: FeatureCollection = { type: "FeatureCollection", features: [] }
    setGeoJsonSurf(emptyCollection)
    setGeoJsonPct(emptyCollection)
    setGeoJsonLine(emptyCollection)

    const fetchData = async () => {
      const partition = commune.pluDocument?.partition
      if (!partition) return

      const cqlFilterPsc = `typepsc IN (${typepsc?.map((type) => `'${type}'`).join(",")}) AND partition='${partition}'`
      const cqlFilterInf = `typeinf IN (${typeinf?.map((type) => `'${type}'`).join(",")}) AND partition='${partition}'`

      const baseUrl = `https://data.geopf.fr/wfs/ows?service=WFS&version=2.0.0&request=GetFeature&outputFormat=application/json&srsName=EPSG:4326`
      const pscTypeName = ["wfs_du:prescription_surf", "wfs_du:prescription_pct", "wfs_du:prescription_lin"]
      const infTypeName = ["wfs_du:info_surf", "wfs_du:info_pct", "wfs_du:info_lin"]

      try {
        const pscUrls =
          typepsc?.length > 0
            ? pscTypeName.map((typeName) =>
                fetch(`${baseUrl}&typeName=${typeName}&cql_filter=${encodeURIComponent(cqlFilterPsc)}`).then((r) =>
                  r.json(),
                ),
              )
            : Array(3)
                .fill(null)
                .map(() => Promise.resolve({ features: [] }))

        const infUrls =
          typeinf?.length > 0
            ? infTypeName.map((typeName) =>
                fetch(`${baseUrl}&typeName=${typeName}&cql_filter=${encodeURIComponent(cqlFilterInf)}`).then((r) =>
                  r.json(),
                ),
              )
            : Array(3)
                .fill(null)
                .map(() => Promise.resolve({ features: [] }))

        const [surfDataPcs, pctDataPcs, lineDataPcs, surfDataInf, pctDataInf, lineDataInf] = await Promise.all([
          ...pscUrls,
          ...infUrls,
        ])

        const addCommonCode = (f: Feature) => ({
          ...f,
          properties: {
            ...f.properties,
            common_code: f.properties?.typeinf ?? f.properties?.typepsc,
          },
        })

        setGeoJsonSurf({
          type: "FeatureCollection",
          features: [...(surfDataPcs.features || []), ...(surfDataInf.features || [])].map(addCommonCode),
        })
        setGeoJsonPct({
          type: "FeatureCollection",
          features: [...(pctDataPcs.features || []), ...(pctDataInf.features || [])].map(addCommonCode),
        })
        setGeoJsonLine({
          type: "FeatureCollection",
          features: [...(lineDataPcs.features || []), ...(lineDataInf.features || [])].map(addCommonCode),
        })
      } catch (err) {
        console.error("Error fetching WFS data:", err)
        setGeoJsonSurf(undefined)
        setGeoJsonPct(undefined)
        setGeoJsonLine(undefined)
      }
    }

    fetchData()
  }, [map, commune, typepsc, typeinf])

  return (
    <>
      <Source id="surface-wfs" type="geojson" data={geoJsonSurf}>
        <Layer
          id="wfs-layer"
          type="fill"
          paint={{
            "fill-color": colors,
            "fill-opacity": 0.6,
          }}
        />
        <Layer
          id="wfs-layer-border"
          type="line"
          paint={{
            "line-color": colors,
            "line-width": 1,
          }}
        />
      </Source>

      <Source id="line-wfs" type="geojson" data={geoJsonLine}>
        <Layer
          id="wfs-layer-line"
          type="line"
          paint={{
            "line-color": colors,
            "line-width": 5,
          }}
        />
      </Source>

      <Source id="pct-wfs" type="geojson" data={geoJsonPct}>
        <Layer
          id="wfs-layer-pct"
          type="circle"
          paint={{
            "circle-radius": 6,
            "circle-color": "#007cbf",
            "circle-stroke-width": 2,
            "circle-stroke-color": "#ffffff",
          }}
        />
      </Source>
    </>
  )
}
